/**
 * Sprint 11.1 — Candidates list, paginated and DB-backed.
 *
 * Migrated away from the legacy `/api/candidates` (STORE+DB merge with no
 * paging) to the v1 paginated contract `/api/v1/candidates`. Filters are
 * mirrored into the URL via `usePagedQueryParams` so deep links and the
 * browser back button behave naturally.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { v1Get } from '@/lib/api';
import { fmtDateIST } from '@/lib/format';
import { PageHeader } from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusPill } from '@/components/StatusPill';
import { Gated } from '@/auth/permissions';
import { displayStageLabel, IQ_ASSESSMENT_LABEL } from '@/lib/stageLabels';
import { formatMasterOptionLabel } from '@/lib/masterDataLabels';

// Sprint 16 — Compact pill rendering the evaluation-lifecycle status from
// the enriched candidates list endpoint (active / closed / none). Replaces
// the legacy ``StatusPill`` driven by ``candidates.status`` which often went
// stale once Evaluations became the source of truth.
function EvalStatusPill({ status }) {
  const tone = (
    status === 'active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : status === 'closed' ? 'bg-slate-100 text-slate-700 border-slate-200'
    : 'bg-amber-100 text-amber-700 border-amber-200'  // 'none' — no evals yet
  );
  const label = (
    status === 'active' ? 'Active'
    : status === 'closed' ? 'All closed'
    : 'No evaluations'
  );
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${tone}`}
      data-testid={`eval-status-pill-${status}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${
        status === 'active' ? 'bg-emerald-500'
        : status === 'closed' ? 'bg-slate-400'
        : 'bg-amber-500'
      }`} />
      {label}
    </span>
  );
}
import { Avatar } from '@/components/Avatar';
import { Search, Plus, ChevronRight } from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import {
  DataTablePagination,
  usePagedQueryParams,
  DEFAULT_PAGE_SIZE,
} from '@/components/DataTablePagination';

// Filter values stay lowercased — the list API compares stage codes that way.
const STAGE_LABEL = {
  resume: 'Resume',
  videobot: 'Video Bot',
  mcq: IQ_ASSESSMENT_LABEL,
  tech_assessment: 'Tech Assessment',
  panel: 'Panel Interview',
  offer: 'Offer',
};

function stageLabel(code) {
  if (!code) return null;
  // Links/assessments store UPPERCASE (MCQ); overlay display via shared helper.
  return displayStageLabel(code);
}

/** One-line current stage; multi-eval details in HoverCard (never stack). */
function CandidateStageCell({ candidate }) {
  const stages = Array.isArray(candidate.evaluation_stages)
    ? candidate.evaluation_stages
    : [];
  const evalCount = candidate.eval_count || 0;

  let primaryLabel = '—';
  if (evalCount > 0) {
    const displayed = stages.find((s) => s.is_displayed);
    primaryLabel = (
      displayed?.stage_label
      || (displayed?.stage_code && stageLabel(displayed.stage_code))
      || (candidate.displayed_stage_code
        ? stageLabel(candidate.displayed_stage_code)
        : null)
      || stages[stages.length - 1]?.stage_label
      || (stages[stages.length - 1]?.stage_code
        ? stageLabel(stages[stages.length - 1].stage_code)
        : null)
      || 'Resume'
    );
  } else if (candidate.has_partner_resume || candidate.displayed_stage_code === 'resume') {
    // Portal registration uploaded a CV before the first evaluation.
    primaryLabel = 'Resume';
  }

  if (stages.length <= 1) {
    return (
      <span className="text-sm font-medium" data-testid="candidate-stage-label">
        {stages.length === 1
          ? (stages[0].stage_label || stageLabel(stages[0].stage_code) || 'Resume')
          : primaryLabel}
      </span>
    );
  }

  const moreCount = stages.length - 1;
  // Newest first for triage in the disclosure panel.
  const disclosureStages = [...stages].reverse();

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="inline-flex max-w-[220px] items-center gap-1.5 rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="candidate-stage-cell"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <span
            className="truncate text-sm font-medium"
            data-testid="candidate-stage-label"
          >
            {primaryLabel}
          </span>
          <span
            className="inline-flex shrink-0 items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground"
            data-testid="candidate-stage-more"
          >
            +{moreCount}
          </span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-[280px] p-2"
        data-testid="candidate-stage-hover"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="mb-1.5 px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Evaluations
        </p>
        <ul className="flex max-h-[240px] flex-col gap-0.5 overflow-y-auto">
          {disclosureStages.map((s) => {
            const label = s.stage_label || stageLabel(s.stage_code) || 'Resume';
            const status = (s.status || '').toLowerCase() === 'active' ? 'active' : 'closed';
            return (
              <li
                key={s.evaluation_id}
                className={`flex items-start justify-between gap-2 rounded-md px-2 py-1.5 ${
                  s.is_displayed ? 'bg-secondary/80' : ''
                }`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium leading-snug">
                    {s.name || 'Evaluation'}
                    {s.is_displayed ? (
                      <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
                        current
                      </span>
                    ) : null}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{label}</p>
                </div>
                <span
                  className={`mt-0.5 inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${
                    status === 'active'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-slate-50 text-slate-600'
                  }`}
                >
                  {status === 'active' ? 'Active' : 'Closed'}
                </span>
              </li>
            );
          })}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'offered', label: 'Offered' },
];

// Sprint 14.1d — Evaluation-status segment. Independent from the candidate
// `status` field above; this one asks "does the candidate have an Active
// evaluation row?".
const EVAL_STATUS_OPTIONS = [
  { value: 'all', label: 'All evaluations' },
  { value: 'has_active', label: 'Has active eval' },
  { value: 'all_closed', label: 'All closed' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return fmtDateIST(iso, { day: '2-digit', month: 'short' }, iso);
  } catch {
    return iso;
  }
};

export default function CandidatesList() {
  const navigate = useNavigate();
  const { params, setPage, setPageSize, setFilter, setManyFilters, resetFilters } =
    usePagedQueryParams({
      initial: { q: '', stage: 'all', role_id: 'all', department_id: 'all', status: 'all', eval_status: 'all' },
    });
  const [searchInput, setSearchInput] = React.useState(() => params.q || '');

  // Debounce the q filter so we don't fire an API call per keystroke.
  React.useEffect(() => {
    const t = setTimeout(() => {
      if ((params.q || '') !== searchInput) setFilter('q', searchInput);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Master data for filter dropdowns — real DB rows, not hardcoded.
  const { data: roles = [] } = useQuery({
    queryKey: ['v1-master-roles'],
    queryFn: () => v1Get('/master/roles'),
    staleTime: 60_000,
  });
  const { data: depts = [] } = useQuery({
    queryKey: ['v1-master-departments'],
    queryFn: () => v1Get('/master/departments'),
    staleTime: 60_000,
  });

  // Paginated candidates query — server returns labels via join.
  const qs = new URLSearchParams();
  qs.set('page', params.page);
  qs.set('page_size', params.page_size);
  if (params.q) qs.set('q', params.q);
  if (params.stage && params.stage !== 'all') qs.set('stage', params.stage);
  if (params.role_id && params.role_id !== 'all') qs.set('role_id', params.role_id);
  if (params.department_id && params.department_id !== 'all') qs.set('department_id', params.department_id);
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  // Sprint 14.1d — Evaluation-status segment filter.
  if (params.eval_status && params.eval_status !== 'all') qs.set('eval_status', params.eval_status);

  const { data, isFetching } = useQuery({
    queryKey: ['v1-candidates', qs.toString()],
    queryFn: () => v1Get(`/candidates?${qs.toString()}`),
    keepPreviousData: true,
  });
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 0;

  const hasActiveFilters =
    (params.q || '') !== '' ||
    params.stage !== 'all' ||
    params.role_id !== 'all' ||
    params.department_id !== 'all' ||
    params.status !== 'all' ||
    params.eval_status !== 'all';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Candidates"
        subtitle={`${total.toLocaleString()} candidates across all pipelines`}
        actions={
          <Gated need="candidate.create">
            <Button
              className="gap-1.5"
              onClick={() => navigate('/portal/candidates/new')}
              data-testid="create-candidate-button"
            >
              <Plus className="h-4 w-4" /> New Candidate
            </Button>
          </Gated>
        }
      />

      <Card className="card-elevated">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
                data-testid="candidate-list-search-input"
              />
            </div>
            <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:flex md:w-auto md:flex-wrap md:items-center">
              <Select value={params.stage} onValueChange={(v) => setFilter('stage', v)}>
                <SelectTrigger className="w-full md:w-[160px]" data-testid="candidate-list-stage-filter">
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stages</SelectItem>
                  {Object.entries(STAGE_LABEL).map(([code, label]) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={params.role_id} onValueChange={(v) => setFilter('role_id', v)}>
                <SelectTrigger className="w-full md:w-[160px]" data-testid="candidate-list-role-filter">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {roles
                    .filter((r) => r.status === 'active')
                    .map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {formatMasterOptionLabel(r)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Select
                value={params.department_id}
                onValueChange={(v) => setFilter('department_id', v)}
              >
                <SelectTrigger className="w-full md:w-[180px]" data-testid="candidate-list-department-filter">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {depts
                    .filter((d) => d.status === 'active')
                    .map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {formatMasterOptionLabel(d)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {/* Sprint 16 — Legacy candidates.status filter removed. The
                  source of truth for lifecycle is now the Evaluations
                  dropdown below (Sprint 14.1d). */}
              {/* Sprint 14.1d — Evaluation-status segment. */}
              <Select value={params.eval_status} onValueChange={(v) => setFilter('eval_status', v)}>
                <SelectTrigger className="w-full md:w-[160px]" data-testid="candidate-list-eval-status-filter">
                  <SelectValue placeholder="Evaluations" />
                </SelectTrigger>
                <SelectContent>
                  {EVAL_STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchInput('');
                  resetFilters();
                }}
                disabled={!hasActiveFilters}
                className="w-full sm:w-auto"
                data-testid="candidate-list-clear-filters-button"
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden card-elevated">
        <Table
          className="min-w-[1040px]"
          data-testid="candidate-table"
          aria-label="Candidates"
        >
          <TableHeader>
            <TableRow className="bg-[hsl(210_40%_98%)] hover:bg-[hsl(210_40%_98%)]">
              <TableHead className="min-w-[220px] whitespace-nowrap">Candidate</TableHead>
              <TableHead className="min-w-[150px] whitespace-nowrap">Role</TableHead>
              <TableHead className="min-w-[140px] whitespace-nowrap">Department</TableHead>
              <TableHead className="min-w-[120px] whitespace-nowrap">Current stage</TableHead>
              <TableHead className="min-w-[110px] whitespace-nowrap">Status</TableHead>
              <TableHead className="min-w-[140px] whitespace-nowrap">Assigned HR</TableHead>
              <TableHead className="min-w-[90px] whitespace-nowrap">Updated</TableHead>
              <TableHead className="min-w-[90px] whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isFetching && items.length === 0 && (
              <>
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            )}
            {!isFetching && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                  {hasActiveFilters
                    ? 'No candidates match these filters.'
                    : 'No candidates yet — click “New Candidate” to add one.'}
                </TableCell>
              </TableRow>
            )}
            {items.map((c) => {
              const navigable = c.detail_navigable !== false;
              return (
              <TableRow
                key={c.id}
                data-testid="candidate-row"
                data-navigable={navigable ? 'true' : 'false'}
                className={navigable ? 'cursor-pointer' : 'cursor-default'}
                onClick={navigable ? () => navigate(`/portal/candidates/${c.id}`) : undefined}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar name={c.full_name} size={32} src={c.photo_url} />
                    <div className="flex min-w-0 flex-col gap-1">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-sm font-semibold leading-tight">{c.full_name}</span>
                        {/* Sprint 16 — Evaluation count badge. Only shown when
                            the candidate has more than 1 evaluation on file,
                            to flag the multi-eval cases without cluttering
                            single-eval rows. */}
                        {c.eval_count > 1 && (
                          <span
                            className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-indigo-700"
                            title={`${c.eval_count} evaluations on file`}
                            data-testid="candidate-eval-count-badge"
                          >
                            {c.eval_count} evals
                          </span>
                        )}
                        {!navigable && (
                          <span
                            className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-800 ring-1 ring-inset ring-amber-200"
                            title="Synced from Candidate Portal — waiting for the candidate to choose a role and evaluation"
                            data-testid="candidate-portal-badge"
                          >
                            Pending — choose evaluation
                          </span>
                        )}
                      </div>
                      <p className="truncate text-xs leading-snug text-muted-foreground">{c.email || '—'}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <span className="inline-flex items-center whitespace-nowrap rounded-md bg-secondary px-2 py-0.5 text-xs font-medium leading-5">
                    {c.role_label || '—'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">{c.department_label || '—'}</span>
                </TableCell>
                <TableCell>
                  <CandidateStageCell candidate={c} />
                </TableCell>
                <TableCell>
                  {/* Sprint 16 — Status reflects evaluation lifecycle, not
                      the legacy candidates.status enum. Active=at-least-one,
                      Closed=all-closed, None=no-evals-yet (rare). */}
                  <EvalStatusPill status={c.eval_status_display} />
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">{c.assigned_hr_name || '—'}</span>
                </TableCell>
                <TableCell>
                  {/* Sprint 16 — "Updated" reflects the most recent
                      evaluation's updated_at, not the stale candidate row. */}
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {fmtDate(c.latest_eval_updated_at || c.updated_at)}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  {navigable ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1 pr-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/portal/candidates/${c.id}`);
                      }}
                      data-testid="candidate-row-view-button"
                    >
                      View <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <span
                      className="text-xs text-muted-foreground"
                      data-testid="candidate-row-view-disabled"
                      title="No evaluation yet — detail unavailable"
                    >
                      —
                    </span>
                  )}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <DataTablePagination
          page={params.page}
          pageSize={params.page_size}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          isLoading={isFetching}
        />
      </Card>
    </div>
  );
}
